import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';

import { connect } from 'react-redux';

import useLocalStorage from '../../hooks/useLocalStorage';

import {
    arrayOf,
    bool,
    func,
    number,
    object,
    oneOf,
    string
} from 'prop-types';

import {
    CustomTreeData,
    FilteringState,
    IntegratedFiltering,
    IntegratedSelection,
    SelectionState,
    TableColumnVisibility,
    TreeDataState,
    IntegratedSorting,
    SortingState,
    DataTypeProvider
} from '@devexpress/dx-react-grid';
import {
    Grid,
    VirtualTable,
    Table,
    TableHeaderRow,
    TableFilterRow,
    TableTreeColumn
} from '@devexpress/dx-react-grid-material-ui';

import CircularProgress from '@material-ui/core/CircularProgress';

import InventoryTableContext from './InventoryTableContext';
import InventoryLeafCellFormatter from './InventoryLeafCellFormatter';
import InventoryTableActionCell from './InventoryTableActionCell';

import useTree from '../../hooks/useTree';
import { getSelectedIds } from '../../helpers/inventoryList';

import classes from './NiagaraInventoryTable.module.css';

const resolution2560 = matchMedia('(min-width:2560px)');
const resolution1980 = matchMedia('(min-width:1980px)');

const defaultFilter = [{}];
  
const styles = theme => ({
    tableStriped: {
        backgroundColor:                '#fff',
        '& tbody tr:nth-of-type(even)': { backgroundColor: '#f4f4f4' }}
});
  
const Columns = [
    { name: 'name', title: 'Name'},
    { name: 'type', title: 'Type' },
    { name: 'dataType', title: 'Data Type' },
    { name: 'sampleInterval', title: 'Interval' },
    { name: 'display', title: 'Display' },
    { name: 'profileName', title: 'Profile' },
    { name: 'action', title: 'Action'}
];

const compareName = (a, b) => {
    if (a && b) {
        const nameA = a.toLowerCase();
        const nameB = b.toLowerCase();

        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        return 0;
    }
};

const LeafOnlyColumns = [
    'dataType',
    'sampleInterval'
];

function getRowId({ oBIXPath }) {
    return oBIXPath;
}

function handleSort(a, b) {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}

function fixItemName(name) {
    const substrings = [{ target: '$20', replacement: ' ' }, { target: '$2d', replacement: '-' }, { target: '$25', replacement: '%' }];

    substrings.forEach(substring => {
        if (name.includes(substring.target)) {
            name = name.replace(substring.target, substring.replacement);
        }
    });

    return name;
}


function getChildRows(row, rootRows) {
    if (!row) {
        return rootRows;
    }
    if (row.items && row.items.length) {
        row.items.forEach(item => {
            item.name = fixItemName(item.name);
        });
        return row.items.sort(handleSort);
    }
    return null;
}

function TableCell(cellProps) {
    switch (cellProps.column.name) {
        case 'action':
            return <InventoryTableActionCell {...cellProps} />;
        default:
            return (
                <Table.Cell
                    {...cellProps}
                />
            );
    }
}

function TableRow({row, ...props}) {
    return (
        <Table.Row
            {...props}
        >
            {
                React.Children.map(props.children, (child, index) => {
                    if (!row.isCreated) return child;

                    return React.cloneElement(child, {
                        ...child.props,
                        style: { color: '#B3B2B1' }
                    });
                })
            }
        </Table.Row>
    );
}

const TableComponentBase = ({ classes, ...restProps }) => (
    <Table.Table {...restProps} className={classes.tableStriped} />
);
  
export const TableComponent = withStyles(styles, { name: 'TableComponent' })(
    TableComponentBase
);
  
const NiagaraInventoryTable = ({
    autoSelectDescendants,
    defaultSelection,
    editable,
    enableLocalStorage,
    hiddenColumns,
    inventory,
    loading,
    onCellChange,
    onDeviceSelection,
    rootId,
    selectedAgentId,
    treeCacheId,
    workbookModeEnabled,
    ...props
}) => {
    const [selection, setSelection] = useState(defaultSelection);
    const tree = useTree(inventory, { rootId, treeCacheId });

    const [selectedNodes, setSelectedNodes] = useLocalStorage('niagaraInventoryTableSelectedNodes', []);
    const [expandedNodes, setExpandedNodes] = useLocalStorage('niagaraInventoryTableExpandedNodes', []);
    const [tableHeight, setTableHeight] = useState('480px');
    const [filters, setFilters] = useState(defaultFilter);
    const [numericColumns] = useState(['sampleInterval']);
    const [numericFilterOperations] = useState([
        'equal',
        'notEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'lessThan',
        'lessThanOrEqual',
    
    ]);

    const update2560TableHeight = () => {
        setTableHeight('68vh');
    };

    const update1980TableHeight = () => {
        if (window.innerWidth <= 1980) {
            setTableHeight('53vh');
        }
        else {
            setTableHeight('68vh');
        }
    };

    useEffect(() => {
        return () => {
            resolution1980.removeEventListener('change', update1980TableHeight);
            resolution2560.removeEventListener('change', update2560TableHeight);
        };

    }, []);

    useEffect(() => {
        resolution2560.addEventListener('change', update2560TableHeight);
        resolution1980.addEventListener('change', update1980TableHeight);
        update1980TableHeight();
    }, []);

    useEffect(() => {
        if (enableLocalStorage) {

            if (selectedNodes) {
                selectedNodes.forEach(item => {
                    if (typeof item !== 'object') {
                        setSelectedNodes([]);
                        return;
                    }
                });
            }

            let filteredNodes = selectedNodes.find(node => node.agentId === selectedAgentId);
            let activeNodes = [];

            if (filteredNodes) {
                activeNodes = filteredNodes.selectedNodes;
            }

            setSelection(activeNodes);
        }
    }, [selectedNodes]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setFilters(defaultFilter);
        }
    }, [workbookModeEnabled]);

    const integratedSortingColumnExtensions = [
        { columnName: 'name', compare: compareName },
    ];

    function handleDeviceSelection(ids) {
        const selectedIds = getSelectedIds(inventory, ids, {
            currentSelectedIds: selection,
            autoSelectDescendants
        });

        setSelection(selectedIds);
        onDeviceSelection(selectedIds);

        const updatedNodes = updateSelectedState(selectedAgentId, selectedIds);

        if (enableLocalStorage) {
            setSelectedNodes(updatedNodes);
        }
    }

    const updateSelectedState = (agentId, selectedIds) => {
        let filteredNodes = [...selectedNodes];
        const index = filteredNodes.findIndex(item => item.agentId === agentId);

        if (index >= 0) {
            filteredNodes[index] = { agentId: agentId, selectedNodes: [...selectedIds] };
        } else {
            filteredNodes.push({ agentId: agentId, selectedNodes: [...selectedIds] });
        }

        return filteredNodes;
    };

    function handleRowToggle(ids) {
        if (enableLocalStorage) {
            setExpandedNodes(ids);
        }
    }

    return (
        <React.Fragment>
            <InventoryTableContext.Provider value={{ onCellChange }}>
                <div className={classes.NiagaraInventoryTable}>
                    {!inventory ?
                        <div className={classes.loader}>
                            <CircularProgress size={50} />
                        </div>
                        :
                    
                        <div className={classes.tableWrapper}>
                            <Grid
                                columns={Columns}
                                getRowId={getRowId}
                                rows={tree}
                            >
                                <DataTypeProvider
                                    for={numericColumns}
                                    availableFilterOperations={numericFilterOperations}
                                />
                                <TreeDataState
                                    defaultExpandedRowIds={expandedNodes}
                                    onExpandedRowIdsChange={handleRowToggle}
                                />
                                <FilteringState 
                                    filters={filters} 
                                    onFiltersChange={setFilters}
                                />
                                <SortingState
                                    defaultSorting={[{
                                        columnName: 'name',
                                        direction:  'asc'
                                    }]}
                                />
                                <SelectionState
                                    selection={selection}
                                    onSelectionChange={handleDeviceSelection}
                                    defaultSelection={defaultSelection}
                                />
                                <CustomTreeData
                                    getChildRows={getChildRows}
                                />
                                <IntegratedFiltering />
                                <IntegratedSelection />
                                <IntegratedSorting
                                    columnExtensions={integratedSortingColumnExtensions}
                                />
                                <InventoryLeafCellFormatter
                                    editable={editable}
                                    for={LeafOnlyColumns}
                                    onCellChange={onCellChange}
                                />
                                <VirtualTable
                                    height={tableHeight}
                                    className={classes.TableContainer}
                                    cellComponent={TableCell}
                                    tableComponent={TableComponent}
                                    rowComponent={TableRow}
                                    columnExtensions={
                                        [
                                            { columnName: 'action', width: 100 },
                                            { columnName: 'name', width: 550 },
                                            { columnName: 'profileName', width: 250 },
                                            { columnName: 'type', width: 250 },
                                            { columnName: 'display', width: 250 }
                                        ]
                                    }
                                />
                                <TableHeaderRow showSortingControls />
                                { workbookModeEnabled ? 
                                    <TableFilterRow showFilterSelector={true} />
                                    : null }
                                <TableColumnVisibility
                                    hiddenColumnNames={hiddenColumns}
                                />
                                <TableTreeColumn
                                    for="name"
                                    showSelectionControls
                                    showSelectAll
                                />
                            </Grid>
                        </div>
                    }
                </div>
            </InventoryTableContext.Provider>
        </React.Fragment>
    );
};

NiagaraInventoryTable.defaultProps = {
    autoSelectDescendants: true,
    editable:              false,
    defaultSelection:      [],
    hiddenColumns:         [],
    loading:               false,
    onCellChange:          () => {},
    onDeviceSelection:     () => {}
};

NiagaraInventoryTable.propTypes = {
    autoSelectDescendants: bool,
    defaultSelection:      arrayOf(string),
    editable:              bool,
    hiddenColumns:         arrayOf(oneOf(Columns.map(({ name }) => name))),
    inventory:             object.isRequired,
    loading:               bool,
    onCellChange:          func,
    onDeviceSelection:     func,
    rootId:                string,
    treeCacheId:           number.isRequired
};

const mapStateToProps = state => ({
    selectedAgentId:     state.basAgent.selectedAgentId,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

export default connect(mapStateToProps, null)(NiagaraInventoryTable);
